import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import type { ImpersonationSession } from '../services/impersonation';
import { Consultancy, User } from '../types';
import { supabase } from '../lib/supabase';
import { apiService, ApiException } from '../services/api';

type SwitchableRole = 'consultant' | 'client' | 'facility_manager';

export interface Bootstrap {
  me: User;
  impersonation: ImpersonationSession | null;
  unread_count: number;
  changelog_latest: {version: string};
  consultancies: Consultancy[] | null;
}

interface AuthContextType {
  bootstrap: Bootstrap | null;
  currentUser: User | null;
  session: Session | null;
  loading: boolean;
  isClient: boolean;
  isConsultant: boolean;
  isFacilityManager: boolean;
  /** Top-of-the-pyramid role. Lands on /admin and can manage every
   * consultancy. Above the consultancy tier; sees everything. */
  isH2okayAdmin: boolean;
  /** True when we have a profile but the user has never completed the
   * first-login onboarding screen. PrivateRoute uses this to redirect
   * everyone to /onboarding until they confirm their name. */
  needsOnboarding: boolean;
  setCurrentUser: (user: User | null) => void;
  signInWithOtp: (email: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  /** No-ops left in place so legacy callers don't break — under real auth
   * the signed-in identity is fixed and no in-app role switch is possible. */
  hasUserWithRole: (role: SwitchableRole) => boolean;
  switchRole: (role: SwitchableRole) => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Local-dev auth bypass, mirroring the backend's AUTH_DISABLED flag.
 *
 * When REACT_APP_AUTH_DISABLED=true the app skips Supabase entirely and
 * loads the profile straight from /bootstrap — the backend (also running
 * with AUTH_DISABLED) answers as its seeded dev user. Never set in
 * production builds; the flag is baked in at compile time.
 */

// One in-flight /bootstrap per user; concurrent callers share it.
let profileRequest: { userId: string; promise: Promise<Bootstrap> } | null = null;
const loadProfileOnce = (userId: string): Promise<Bootstrap> => {
  if (profileRequest?.userId === userId) return profileRequest.promise;
  const promise = apiService.get<Bootstrap>('/bootstrap').finally(() => {
    if (profileRequest?.promise === promise) profileRequest = null;
  });
  profileRequest = { userId, promise };
  return promise;
};

export const AUTH_DISABLED = process.env.REACT_APP_AUTH_DISABLED === 'true';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Initial session load + subscribe to auth changes.
  useEffect(() => {
    let cancelled = false;

    if (AUTH_DISABLED) {
      apiService
        .get<Bootstrap>('/bootstrap')
        .then((user) => {
          if (!cancelled) {
            setCurrentUser(user.me);
            setBootstrap(user);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('AUTH_DISABLED profile load failed:', err);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      if (!data.session) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (!next) {
        setCurrentUser(null);
        setBootstrap(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  // Whenever there's a session, fetch / upsert the local profile via /api/bootstrap.
  // Keyed on the signed-in user, not the session object: Supabase hands out a
  // new session on every token refresh and tab focus, and each change used to
  // refire /bootstrap (5-8 calls per page load).
  const sessionUserId = session?.user?.id;
  useEffect(() => {
    if (!sessionUserId) return;
    let cancelled = false;
    setLoading(true);
    loadProfileOnce(sessionUserId)
      .then((user) => {
        if (!cancelled) {
          setCurrentUser(user.me);
          setBootstrap(user);
        }
      })
      .catch(async (err) => {
        if (cancelled) return;
        // 401 here means the JWT is no longer valid; force a re-login.
        if (err instanceof ApiException && err.statusCode === 401) {
          await supabase.auth.signOut();
        }
        // 403 means the user is signed in to Supabase but has no invite/profile.
        // Leave currentUser null; PrivateRoute will keep them on a friendly screen.
        // eslint-disable-next-line no-console
        console.error('Error loading current user:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionUserId]);

  const signInWithOtp = useCallback(
    async (email: string): Promise<{ error?: string }> => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      return error ? { error: error.message } : {};
    },
    [],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setBootstrap(null);
    setSession(null);
  }, []);

  // Demo-era helpers: kept on the interface so existing callers compile, but
  // they are inert under real auth. Header.tsx uses these to hide the toggle.
  const hasUserWithRole = useCallback((_role: SwitchableRole) => false, []);
  const switchRole = useCallback(
    async (_role: SwitchableRole) => null,
    [],
  );

  const role = currentUser?.role ?? session?.user?.app_metadata?.role ?? session?.user?.user_metadata?.role;
  const isClient = role === 'client';
  const isConsultant = role === 'consultant';
  const isFacilityManager = role === 'facility_manager';
  const isH2okayAdmin = role === 'h2okay_admin';
  const needsOnboarding = !!currentUser && !currentUser.profile_completed_at;

  return (
    <AuthContext.Provider
      value={{
        bootstrap,
        currentUser,
        session,
        loading,
        isClient,
        isConsultant,
        isFacilityManager,
        isH2okayAdmin,
        needsOnboarding,
        setCurrentUser,
        signInWithOtp,
        signOut,
        hasUserWithRole,
        switchRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
